import { useTranslation } from "react-i18next";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { AvatarUpload } from "@libs/zapi-ui/ds/components/avatar-upload";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react";
import { useState, useEffect, useId, type FormEvent } from "react";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { toast } from "sonner";
import { getInitials } from "../lib/getInitials";
import { useUpdateUserProfile } from "../services/useUpdateUserProfile";
import { useDeleteUserPhoto } from "../services/useDeleteUserPhoto";
import {
  SettingsHeader,
  SettingsRow,
  SettingsSection,
  SettingsSectionActions,
  SettingsValue
} from "./blocks/SettingsLayout";

export function UserSettings() {
  const { t } = useTranslation(["settings", "common"]);
  const { firstName, lastName, email, profileImageUrl, refetch } = useMeStore();
  const formId = useId();
  const firstNameId = useId();
  const lastNameId = useId();

  const { mutate: updateUserProfile, isPending: isSaving } = useUpdateUserProfile();
  const { mutate: deleteUserPhoto, isPending: isDeleting } = useDeleteUserPhoto();

  const [formData, setFormData] = useState({ firstName: "", lastName: "" });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({ firstName: firstName ?? "", lastName: lastName ?? "" });
  }, [firstName, lastName]);

  const isChanged = formData.firstName !== firstName || formData.lastName !== lastName || selectedFile !== null;
  const isFirstNameMissing = !formData.firstName.trim();
  const isLastNameMissing = !formData.lastName.trim();
  const isBusy = isSaving || isDeleting;

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error(t("profile.invalidFileType"));
      return t("profile.invalidFileType");
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("profile.fileTooLarge"));
      return t("profile.fileTooLarge");
    }

    return null;
  }

  function fileSelectHandler(file: File) {
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function clearSelectedFile() {
    setSelectedFile(null);
    setPreviewUrl(null);
  }

  function handleDiscard() {
    setFormData({ firstName: firstName ?? "", lastName: lastName ?? "" });
    clearSelectedFile();
    setUploadError(null);
  }

  const handleRemovePhoto = async () => {
    setUploadError(null);

    deleteUserPhoto(undefined, {
      onSuccess: async () => {
        toast.success(t("profile.photoRemoved"));
        if (refetch) await refetch();
      },
      onError: (error) => {
        console.error("Failed to remove profile photo:", error);
        const err = error as { response?: { data?: { message?: string } } };
        setUploadError(err.response?.data?.message || t("profile.photoRemoveFailed"));
        toast.error(t("profile.photoRemoveFailed"));
      }
    });
  };

  const handleSaveChanges = async (event: FormEvent) => {
    event.preventDefault();
    if (isFirstNameMissing || isLastNameMissing) return;
    setUploadError(null);

    updateUserProfile(
      {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        file: selectedFile ?? undefined
      },
      {
        onSuccess: async () => {
          toast.success(t("profile.updated"));
          if (refetch) await refetch();
          clearSelectedFile();
        },
        onError: (error) => {
          toast.error(t("profile.updateFailed"));
          console.error("Failed to update profile:", error);
        }
      }
    );
  };

  return (
    <>
      <SettingsHeader
        title={t("tabs.profile")}
        actions={
          <SettingsSectionActions status={isChanged ? t("redesign.unsaved") : undefined}>
            {isChanged && (
              <Button type="button" variant="ghost" shape="pill" disabled={isBusy} onClick={handleDiscard}>
                {t("redesign.discard")}
              </Button>
            )}
            <Button
              type="submit"
              form={formId}
              shape="pill"
              data-testid="user-settings-save-button"
              disabled={isBusy || isFirstNameMissing || isLastNameMissing}
            >
              {isSaving && <Spinner data-icon="inline-start" aria-label={t("common:loading")} />}
              {t("common.save")}
            </Button>
          </SettingsSectionActions>
        }
      />
      <SettingsSection title={t("redesign.profile.title")}>
        <AvatarUpload
          src={previewUrl ?? profileImageUrl}
          fallback={getInitials(firstName ?? "", lastName ?? "")}
          uploadLabel={t("redesign.profile.uploadPhoto")}
          removeLabel={t("redesign.profile.removePhoto")}
          removingLabel={t("profile.removing")}
          hint={t("redesign.photoHint")}
          error={uploadError}
          pending={selectedFile !== null}
          removing={isDeleting}
          disabled={isSaving}
          onSelect={fileSelectHandler}
          onRemove={profileImageUrl ? handleRemovePhoto : undefined}
        />
        <form id={formId} onSubmit={handleSaveChanges}>
          <FieldGroup className="grid gap-4 sm:grid-cols-2">
            <Field data-invalid={isFirstNameMissing || undefined}>
              <FieldLabel htmlFor={firstNameId}>{t("redesign.profile.firstName")}</FieldLabel>
              <Input
                id={firstNameId}
                autoComplete="given-name"
                data-testid="user-settings-first-name-input"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={isBusy}
                aria-invalid={isFirstNameMissing || undefined}
              />
              {isFirstNameMissing && <FieldError>{t("invite.firstNameRequired")}</FieldError>}
            </Field>
            <Field data-invalid={isLastNameMissing || undefined}>
              <FieldLabel htmlFor={lastNameId}>{t("redesign.profile.lastName")}</FieldLabel>
              <Input
                id={lastNameId}
                autoComplete="family-name"
                data-testid="user-settings-last-name-input"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={isBusy}
                aria-invalid={isLastNameMissing || undefined}
              />
              {isLastNameMissing && <FieldError>{t("invite.lastNameRequired")}</FieldError>}
            </Field>
          </FieldGroup>
        </form>
        {email && (
          <SettingsRow label={t("redesign.profile.email")} description={t("redesign.profile.emailHint")}>
            <SettingsValue icon={EnvelopeSimpleIcon}>{email}</SettingsValue>
          </SettingsRow>
        )}
      </SettingsSection>
    </>
  );
}
